import { ReactNode, useEffect, useState } from 'react';
// import Container from '../Container';
import { Container } from 'semantic-ui-react';
import { usePortfolioContext } from '~/hooks';
import { useLocation } from 'react-router-dom';
import PageHeader from '../PageHeader';
import { PageHeaderProvider } from '../PageHeader/PageHeaderProvider';
import { restorePortfolioNavScroll } from '~/utils/portfolioNavScroll';
import ManageProjectsDropdown from './ManageProjectsDropdown';
import { DealStates } from '~/constants';
import { Label } from 'semantic-ui-react';
import PageLoader from '../PageLoader';
import { useRequiredResponseCountsQuery } from '~/requests/projects/useRequiredResponseCounts';
import PortfolioSubmissionButtons from './components/PortfolioSubmissionButtons';
import { usePermissions } from '~/requests/permissions/usePermissions';
import { useDataroomQuery } from '~/requests/dataroom/useDataroomQuery';
import { getDataroomCounts } from '~/utils/calculators/dataroomCounts';
import { useProjectTeamQuery } from '~/requests/team';
import AdditionalActions from '../AdditionalActions';
import ArchiveAction from '../AdditionalActions/ArchiveAction';
import AdminAutopricingLinkAction from '../AdditionalActions/AdminAutopricingLinkAction';
import OwnershipPicker from '~/components/OwnershipPicker';
import Timestamp from '~/components/Timestamp';
import { getMarketplaceListingById } from '~/api/marketplace';

const PortfolioPageContent = function ({ children }: { children: ReactNode }) {
  const { permissions } = usePermissions();
  const { id, portfolio, portfolioState } = usePortfolioContext();
  const { pathname } = useLocation();
  const portfolioEditable = portfolioState?.latestState < DealStates.IN_DILIGENCE;
  const isInvestor = !permissions.isAdmin && permissions.hasPortfolioInvestorAccess;
  const { data: dataroom } = useDataroomQuery({
    onError() { /* user may not have dataroom access */ },
  });
  const dataroomCounts = dataroom ? getDataroomCounts(dataroom.documents) : null;
  const showDataroomTab = permissions?.canReadDataroom
    && !(isInvestor && portfolioState?.latestState < DealStates.IN_DILIGENCE && !dataroomCounts?.uploaded);
  const [listing, setListing] = useState<any>(null);

  const { data: requiredResponseCountsData, isFetching } = useRequiredResponseCountsQuery();
  const requiredResponseCounts = requiredResponseCountsData?.reduce((acc: number, curr: any) => acc + curr.count, 0) ?? 0;

  const [showBiddingTab, setShowBiddingTab] = useState<boolean>(false);

  useEffect(() => {
    fetchListing();
    if (portfolioState?.earliestState >= DealStates.IN_DILIGENCE) {
      setShowBiddingTab(false);
      return;
    }
    if (permissions?.isAdmin) {
      setShowBiddingTab(true);
      return;
    }
    if (portfolioState?.earliestState < DealStates.IN_REVIEW) {
      setShowBiddingTab(false);
    } else {
      setShowBiddingTab(true);
    }
  }, [portfolioState, permissions?.isAdmin]);

  const fetchListing = async () => {
    try {
      const data = await getMarketplaceListingById({ portfolioId: portfolio.id });
      setListing(data.isVisible ? data : null);
    } catch (err) {
      setListing(null);
    }
  };

  useEffect(() => {
    if (portfolio?.id) {
      restorePortfolioNavScroll();
    }
  }, [portfolio?.id]);

  return (
    <Container>
      <div className="metadata-header">
        {permissions.isAdmin && <OwnershipPicker />}
        <div><Timestamp portfolio={portfolio} /></div>
      </div>
      <PageHeader
        portfolio={portfolio}
        content={
          <div style={{
            ...(pathname !== `/portfolio/${id}/review` ? { position: 'relative', height: 0, paddingTop: 0, zIndex: -1, opacity: 0 } : {})
          }}>
          </div>
        }
        navTabs={[{
          path: `/portfolio/${id}/review`,
          content: 'Summary'
        }, (showBiddingTab) && {
          path: `/portfolio/${id}/bidding`,
          content: 'Bidding'
        }, {
          path: `/portfolio/${id}/messages`,
          content: (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div>Messages</div>
              {isFetching ? null : (
                pathname !== `/portfolio/${portfolio.id}/messages` && requiredResponseCounts > 0 && (
                  <Label circular color='red' style={{ marginLeft: 'var(--x-small)' }}>
                    {requiredResponseCounts}
                  </Label>
                ))}
            </div>
          ),
        },
        showDataroomTab ? {
          path: `/portfolio/${id}/dataroom`,
          content: 'Data Room'
        } : null,
        permissions.canManageProjectTeam && {
          path: `/portfolio/${id}/team`,
          content: 'Team',
        }, (permissions.isAdmin || (permissions.isDeveloper && listing)) && {
          path: `/portfolio/${portfolio.id}/listing`,
          content: 'Listing'
        }
        ]}
        secondaryNavContent={
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--x-small)' }}>
            {portfolioEditable && !isInvestor && <ManageProjectsDropdown />}
            {!portfolio.isArchived && <PortfolioSubmissionButtons />}
            <AdditionalActions>
              {permissions.hasArchiveActions && <ArchiveAction />}
              {permissions.isAdmin && <AdminAutopricingLinkAction />}
            </AdditionalActions>
          </div>
        }
      />
      <div>
        {children}
      </div>
    </Container>
  );
};

export default function PortfolioPage({ children }: { children: ReactNode }) {
  const { portfolio, fetchStatus } = usePortfolioContext();
  const { status: permissionsStatus } = usePermissions();
  const { status: teamStatus } = useProjectTeamQuery();

  if (fetchStatus === 'loading' || permissionsStatus === 'loading' || teamStatus === 'loading') {
    return <div style={{ width: '100%', position: 'relative', height: '100vh' }}><PageLoader /></div>;
  } else if (portfolio?.id) {
    return (
      <PageHeaderProvider>
        <PortfolioPageContent>
          {children}
        </PortfolioPageContent>
      </PageHeaderProvider>
    );
  } else {
    return null;
  }
}
